import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { LOOP54_DEFAULT_PAGE_SIZE } from '../components/Pagination/PaginationLoop54';
import { generateUUID } from '../components/utils';
import { useConfig } from '../config';
import { useDebounce } from '../hooks/useDebounce';
import { transformLoopResponseToApiProducts } from '../state/api';
import { MetaContainer } from '../state/meta';
import { ApiProduct, Loop54SearchRequestBody } from '../types';

const INITIAL_RESULTS = {
	items: [],
	total: 0,
	searchedQuery: '',
	showResults: false,
};

type AddProductSearchReturn = {
	results: ProductSearchResults;
	query: string;
	setQuery: React.Dispatch<React.SetStateAction<string>>;
	loading: boolean;
	setPage: React.Dispatch<React.SetStateAction<number>>;
	areMoreResults: boolean;
};

export type ProductSearchResults = {
	items: ApiProduct[];
	total: number;
	searchedQuery: string;
	showResults: boolean;
};

export const useAddProductSearch = (): AddProductSearchReturn => {
	const [query, setQuery] = useState('');
	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState<ProductSearchResults>(INITIAL_RESULTS);
	const [page, setPage] = useState(0);
	const [areMoreResults, setAreMoreResults] = useState(false);
	const debouncedQuery: string = useDebounce<string>(query, 500);
	const { loop54Api } = useConfig();
	const { searchProfileId, randomizeSearchProfileId } = MetaContainer.useContainer();
	/* the search whose answer the screen is waiting for, and the query the page belongs to */
	const inFlight = useRef(0);
	const pagedQuery = useRef<string | null>(null);
	/* what is in the box right now, which the request that is out has no way of knowing */
	const typed = useRef(query);

	useEffect(() => {
		typed.current = query;

		if (query === '') {
			setLoading(false);
			setAreMoreResults(false);
			setResults(INITIAL_RESULTS);
		}
	}, [query]);

	/*
	 * What is on screen stays there until the next search answers. Emptying it here is what made
	 * the modal shrink to the search box and back on every keystroke, and the body greys out while
	 * a search is in flight anyway. An emptied box is a different matter: the effect above clears it.
	 */
	useEffect(() => {
		setPage(0);
		// the page resets a render before the request goes out, and the screen is waiting all the same
		if (debouncedQuery) setLoading(true);
	}, [debouncedQuery]);

	useEffect(() => {
		const controller = new AbortController();
		const currentQuery = debouncedQuery;
		const currentPage = page;
		const doSearch = async () => {
			const run = ++inFlight.current;

			try {
				setLoading(true);
				let loop54SearchPayload: Loop54SearchRequestBody = {
					query: debouncedQuery,
					resultsOptions: {
						take: LOOP54_DEFAULT_PAGE_SIZE,
						skip: page > 0 ? LOOP54_DEFAULT_PAGE_SIZE * page : page,
					},
					relatedResultsOptions: {
						take: 0,
						skip: 0,
					},
				};
				const loop54PostHeaders = {
					'Api-Version': 'V3',
					'User-id': randomizeSearchProfileId ? generateUUID() : searchProfileId,
				};
				let { data, status } = await axios.post(
					loop54Api + 'search',
					{ ...loop54SearchPayload },
					{ withCredentials: false, signal: controller.signal, headers: { ...loop54PostHeaders } }
				);

				// Ignore stale responses from older query/page requests.
				if (currentQuery !== debouncedQuery || currentPage !== page) return;

				/*
				 * An emptied box is answered by the effect above, half a second before the debounce
				 * lets go of the query this request was made for. Putting the products back now
				 * would leave a search heading and its hits standing under an empty field.
				 */
				if (typed.current === '') return;

				if (status === 200) {
					const { results } = data;
					const total = results.count;
					/*
					 * Against the size actually asked for. This counted in 32s while the request
					 * takes 24, so a search with 25 to 32 hits offered no "load more" and the tail
					 * of it could not be reached at all.
					 */
					setAreMoreResults((page + 1) * LOOP54_DEFAULT_PAGE_SIZE < total);
					let transformedProducts: ApiProduct[];

					if (data && data.results) {
						transformedProducts = transformLoopResponseToApiProducts(data.results.items);
						data.results.items = transformedProducts;
					}

					setResults((prev) => {
						return {
							// a new search replaces what was there; only a further page adds to it
							items:
								page === 0
									? results.items.map((item: ApiProduct) => item)
									: [...prev.items, ...results.items.map((item: ApiProduct) => item)],
							total,
							searchedQuery: debouncedQuery,
							showResults: true,
						};
					});
				} else {
					console.error(`HTTP error! Status: ${data.status}`);

					if (typed.current === currentQuery && currentPage === 0) {
						setResults(INITIAL_RESULTS);
						setAreMoreResults(false);
					}
				}
			} catch (error) {
				// an abort is this effect making way for a newer search, which has the screen now
				if (axios.isCancel(error)) return;

				console.error(error);

				/*
				 * The search meant to replace what is on screen never arrived. Holding the old
				 * products would leave them looking like this query's, under a "load more" that
				 * would fetch the next page of the new one and add it to them.
				 *
				 * Unless the customer has typed on since: then something newer is on its way, and
				 * emptying the modal in front of them is the collapse this all exists to avoid.
				 */
				if (typed.current === currentQuery && currentPage === 0) {
					setResults(INITIAL_RESULTS);
					setAreMoreResults(false);
				}
			} finally {
				// a request that has been overtaken does not get to say the waiting is over
				if (run === inFlight.current) setLoading(false);
			}
		};

		/*
		 * The page belongs to the query it was asked for. A new query resets it, but that lands a
		 * render later, so without this the old page number goes out against the new query first -
		 * a request that is aborted a moment later, having already taken the loading state with it.
		 */
		if (page > 0 && pagedQuery.current !== debouncedQuery) return;

		if (debouncedQuery) {
			pagedQuery.current = debouncedQuery;
			doSearch();
		}

		return () => controller.abort();
	}, [debouncedQuery, page]);

	/* the wait starts at the keystroke, not when the debounce lets the request go */
	const waiting = loading || (query !== '' && query !== debouncedQuery);

	return { results, query, setQuery, loading: waiting, setPage, areMoreResults };
};
